/**
 * Source Type Badge (Phase 12.3)
 *
 * Displays the source type of an assessment schedule/template.
 * Used on the Templates page (formerly Assessment Schedules).
 */

import { FileText, MessageSquare, CheckSquare, Gauge } from 'lucide-react';

const sourceTypeConfig = {
  sop: {
    label: 'SOP',
    icon: FileText,
    classes: 'bg-blue-50 text-blue-700 border-blue-200',
  },
  ai_generated: {
    label: 'AI Generated',
    icon: MessageSquare,
    classes: 'bg-purple-50 text-purple-700 border-purple-200',
  },
  checklist: {
    label: 'Checklist',
    icon: CheckSquare,
    classes: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  },
  kpi: {
    label: 'KPI',
    icon: Gauge,
    classes: 'bg-amber-50 text-amber-700 border-amber-200',
  },
} as const;

type SourceType = keyof typeof sourceTypeConfig;

interface SourceTypeBadgeProps {
  sourceType: string;
  size?: 'sm' | 'md';
}

export function SourceTypeBadge({ sourceType, size = 'sm' }: SourceTypeBadgeProps) {
  const config = sourceTypeConfig[sourceType as SourceType] ?? sourceTypeConfig.sop;
  const Icon = config.icon;

  if (size === 'sm') {
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${config.classes}`}>
        <Icon size={10} />
        {config.label}
      </span>
    );
  }

  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold border ${config.classes}`}>
      <Icon size={14} />
      {config.label}
    </span>
  );
}
